import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { userService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  UserGroupIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  PencilIcon,
  TrashIcon,
  EnvelopeIcon,
  ShieldCheckIcon,
  KeyIcon,
  AdjustmentsHorizontalIcon,
  EyeIcon,
  EyeSlashIcon,
  ClipboardDocumentIcon,
  CheckCircleIcon,
  XMarkIcon,
  ArrowUpTrayIcon,
  ListBulletIcon,
  ShareIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { Spinner } from '../components/Spinner';
import { EditUserModal } from '../components/EditUserModal';
import { ResetPasswordModal } from '../components/ResetPasswordModal';
import { UserPermissionsModal } from '../components/UserPermissionsModal';
import BulkUserUploadModal from '../components/BulkUserUploadModal';
import TeamTree from '../components/TeamTree';

// Roles visible to each role (mirrors backend ROLE_HIERARCHY)
const ROLE_HIERARCHY = {
  super_admin: ['super_admin', 'admin', 'dealer', 'sales_executive'],
  admin:       ['admin', 'dealer', 'sales_executive'],
  dealer:      ['dealer', 'sales_executive'],
  sales_executive: [],
};

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  dealer: 'Dealer',
  sales_executive: 'Sales Executive',
};

function canManageUser(actorRole, targetRole) {
  return (ROLE_HIERARCHY[actorRole] || []).includes(targetRole);
}

const EMPTY_FILTERS = { search: '', role: '', status: '', phone: '', email: '', userCode: '', under: '', page: 1 };
const TEAM_RANK = { super_admin: 0, admin: 1, dealer: 2 };

function useDebounced(value, ms = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

// ── Credentials Modal ────────────────────────────────────────────────────────
function CredentialsModal({ user, onClose }) {
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState('');

  const { data, isLoading, error } = useQuery(
    ['userCredentials', user._id],
    () => userService.getCredentials(user._id),
    { enabled: !!user._id, retry: false, staleTime: 0 }
  );
  const cred = data?.data?.data || {};

  const copy = (label, value) => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(label);
      setTimeout(() => setCopied(''), 1500);
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">Login Credentials</h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-5 space-y-4">
          {isLoading ? (
            <div className="flex justify-center py-6"><Spinner /></div>
          ) : error ? (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
              {error.response?.data?.message || 'Failed to load credentials'}
            </p>
          ) : (
            <>
              <div className="text-sm text-gray-600">
                <span className="font-medium text-gray-800">{cred.firstName} {cred.lastName}</span>
                <span className="ml-2 text-xs px-2 py-0.5 bg-gray-100 rounded uppercase tracking-wide">{cred.role?.replace('_', ' ')}</span>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
                <div className="flex items-center bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                  <span className="flex-1 text-sm font-mono text-gray-700 break-all">{cred.email}</span>
                  <button onClick={() => copy('email', cred.email)} className="ml-2 p-1 text-gray-400 hover:text-primary-600">
                    {copied === 'email'
                      ? <CheckCircleIcon className="h-4 w-4 text-green-500" />
                      : <ClipboardDocumentIcon className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Password</label>
                {cred.password ? (
                  <div className="flex items-center bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                    <span className="flex-1 text-sm font-mono text-gray-700 break-all">
                      {showPassword ? cred.password : '•'.repeat(Math.min(cred.password.length, 12))}
                    </span>
                    <button onClick={() => setShowPassword(s => !s)} className="ml-2 p-1 text-gray-400 hover:text-primary-600">
                      {showPassword ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                    </button>
                    <button onClick={() => copy('password', cred.password)} className="ml-1 p-1 text-gray-400 hover:text-primary-600">
                      {copied === 'password'
                        ? <CheckCircleIcon className="h-4 w-4 text-green-500" />
                        : <ClipboardDocumentIcon className="h-4 w-4" />}
                    </button>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                    Password not stored. Reset the password to set a new one.
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-100 flex justify-end">
          <button onClick={onClose} className="btn-outline-secondary text-sm">Close</button>
        </div>
      </div>
    </div>
  );
}

export const Users = () => {
  const { hasPermission, user: currentUser } = useAuth();
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [view, setView] = useState('list');   // 'list' | 'team' (who reports to whom)

  // Typed filters apply once typing pauses, rather than a fetch per keystroke
  const typed = useDebounced(JSON.stringify({
    search: filters.search, phone: filters.phone, email: filters.email, userCode: filters.userCode,
  }));
  const queryParams = Object.fromEntries(
    Object.entries({ ...filters, ...JSON.parse(typed) }).filter(([, v]) => v !== '' && v != null)
  );
  const filtersActive = Object.entries(filters).some(([k, v]) => k !== 'page' && v);

  // Everyone visible, for the "Team of" filter and the team view
  const { data: hierarchyData } = useQuery('hierarchyUsers', () => userService.getHierarchy(), { staleTime: 60 * 1000 });
  const hierarchyUsers = hierarchyData?.data?.data?.users || hierarchyData?.data?.users || [];
  const teamLeads = hierarchyUsers
    .filter(u => ['super_admin', 'admin', 'dealer'].includes(u.role))
    .sort((a, b) => (TEAM_RANK[a.role] - TEAM_RANK[b.role]) || `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`));
  const underUser = filters.under ? hierarchyUsers.find(u => String(u._id) === String(filters.under)) : null;

  // Download: everyone the current filters match (not just this page), with
  // login details. The server leaves out passwords the viewer may not see.
  const [downloading, setDownloading] = useState(false);
  const downloadUsers = async () => {
    setDownloading(true);
    try {
      const { page, limit, ...params } = queryParams;
      const res = await userService.exportUsers(params);
      const rows = res.data?.data?.users || [];
      if (!rows.length) {
        toast.error('No users to download');
        return;
      }
      const when = (d) => (d ? format(new Date(d), 'dd-MM-yyyy HH:mm') : '');
      const sheetRows = rows.map(u => ({
        'User ID':        u.userCode,
        'First Name':     u.firstName,
        'Last Name':      u.lastName,
        'Email':          u.email,
        'Phone':          u.phone,
        'Password':       u.password,
        'Role':           ROLE_LABELS[u.role] || u.role,
        'Status':         u.status,
        'Reports To':     u.reportsTo,
        'Reports To ID':  u.reportsToCode,
        'Reports To Role': ROLE_LABELS[u.reportsToRole] || u.reportsToRole,
        'Brands':         u.brands.join(', '),
        'Cities':         u.cities.join(', '),
        'Pincodes':       u.pincodes.join(', '),
        'Lead Pack':      u.leadPack,
        'In Auto-Distribution': u.inDistribution === null ? '' : (u.inDistribution ? 'Yes' : 'No'),
        'Previous User IDs': u.previousUserCodes.join(', '),
        'Last Login':     when(u.lastLogin),
        'Created':        when(u.createdAt),
      }));
      const ws = XLSX.utils.json_to_sheet(sheetRows);
      ws['!cols'] = Object.keys(sheetRows[0]).map(k => ({ wch: Math.max(k.length, 14) }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Users');
      XLSX.writeFile(wb, `users-${format(new Date(), 'dd-MM-yyyy')}.xlsx`);
      toast.success(`${rows.length} user${rows.length === 1 ? '' : 's'} downloaded — the file has passwords, keep it safe`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const showTeamOf = (user) => {
    setFilters({ ...EMPTY_FILTERS, under: String(user._id) });
    setView('list');
  };

  // Modal state
  const [editingUser, setEditingUser] = useState(null);
  const [resettingPasswordUser, setResettingPasswordUser] = useState(null);
  const [managingPermissionsUser, setManagingPermissionsUser] = useState(null);
  const [viewingCredentialsUser, setViewingCredentialsUser] = useState(null);
  const [showBulkUpload, setShowBulkUpload] = useState(false);

  const { data: usersData, isLoading } = useQuery(
    ['users', queryParams],
    () => userService.getUsers(queryParams),
    { keepPreviousData: true }
  );

  const deleteUserMutation = useMutation(userService.deleteUser, {
    onSuccess: () => {
      toast.success('User deleted successfully');
      queryClient.invalidateQueries('users');
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Failed to delete user');
    },
  });

  const updateUserMutation = useMutation(
    ({ id, data }) => userService.updateUser(id, data),
    {
      onSuccess: () => {
        toast.success('User updated successfully');
        queryClient.invalidateQueries('users');
      },
    }
  );

  const resetPasswordMutation = useMutation(
    ({ id, data }) => userService.resetPassword(id, data),
    {
      onSuccess: () => toast.success('Password reset successfully'),
    }
  );

  const updatePermissionsMutation = useMutation(
    ({ id, permissions }) => userService.updatePermissions(id, permissions),
    {
      onSuccess: () => {
        toast.success('Permissions updated successfully');
        queryClient.invalidateQueries('users');
      },
    }
  );

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      deleteUserMutation.mutate(id);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      super_admin: { color: 'badge-danger', text: 'Super Admin' },
      admin: { color: 'badge-warning', text: 'Admin' },
      dealer: { color: 'badge-primary', text: 'Dealer' },
      sales_executive: { color: 'badge-secondary', text: 'Sales Executive' },
    };
    const config = roleConfig[role] || roleConfig.sales_executive;
    return <span className={`badge ${config.color}`}>{config.text}</span>;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'badge-success', text: 'Active' },
      inactive: { color: 'badge-secondary', text: 'Inactive' },
      suspended: { color: 'badge-danger', text: 'Suspended' },
    };
    const config = statusConfig[status] || statusConfig.inactive;
    return <span className={`badge ${config.color}`}>{config.text}</span>;
  };

  // Extract users and pagination from response
  let users = [];
  let pagination = {};
  if (usersData?.data) {
    if (usersData.data.users) {
      users = usersData.data.users;
    } else if (Array.isArray(usersData.data)) {
      users = usersData.data;
    } else if (usersData.data.data?.users) {
      users = usersData.data.data.users;
    }
    pagination = usersData.data.data?.pagination || usersData.data.pagination || {};
  }
  const usage = usersData?.data?.data?.usage || usersData?.data?.usage || null;

  // Roles the current user can filter by
  const filterableRoles = ROLE_HIERARCHY[currentUser.role] || [];

  if (!hasPermission('users', 'read')) {
    return (
      <div className="text-center">
        <ShieldCheckIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h2 className="mt-2 text-lg font-medium text-gray-900">Access Denied</h2>
        <p className="mt-1 text-gray-600">You don't have permission to view users.</p>
      </div>
    );
  }

  const pageTitle = currentUser.role === 'manager' ? 'Salespersons' : 'Users';
  const pageDesc = currentUser.role === 'manager'
    ? 'Manage your sales team members'
    : 'Manage your organization users';

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{pageTitle}</h1>
            <p className="mt-2 text-gray-600">{pageDesc}</p>
            {usage && currentUser.role === 'super_admin' && (
              <span className={`mt-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                usage.used >= usage.limit ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
              }`}>
                {usage.used} / {usage.limit} users used
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={downloadUsers}
              disabled={downloading}
              className="btn-outline-primary disabled:opacity-60"
              title={filtersActive ? 'Downloads the users matching your filters' : 'Downloads all users you can see'}
            >
              {downloading
                ? <Spinner size="sm" className="mr-2" />
                : <ArrowDownTrayIcon className="h-4 w-4 mr-2" />}
              Download
            </button>
            {hasPermission('users', 'create') && hasPermission('users', 'bulkUpload') && (
              <button onClick={() => setShowBulkUpload(true)} className="btn-outline-primary">
                <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
                Bulk Upload
              </button>
            )}
            {hasPermission('users', 'create') && (
              <Link to="/users/new" className="btn-primary">
                <PlusIcon className="h-4 w-4 mr-2" />
                {currentUser.role === 'manager' ? 'Add Salesperson' : 'Add User'}
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* List / Team */}
      <div className="mb-4 inline-flex rounded-lg border border-gray-300 bg-white p-0.5">
        {[
          { key: 'list', label: 'List', Icon: ListBulletIcon },
          { key: 'team', label: 'Team', Icon: ShareIcon },
        ].map(({ key, label, Icon }) => (
          <button
            key={key}
            onClick={() => setView(key)}
            className={`flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-md ${
              view === key ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {view === 'team' ? (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <TeamTree onShowTeam={showTeamOf} />
        </div>
      ) : (<>
      {/* Filters */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="px-4 py-4 sm:px-6 sm:flex sm:items-center sm:justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search by name, email or user ID..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>
              </div>
              <button onClick={() => setShowFilters(!showFilters)} className="btn-outline-primary">
                <FunnelIcon className="h-4 w-4 mr-2" />
                Filters
              </button>
              {filtersActive && (
                <button onClick={() => setFilters(EMPTY_FILTERS)} className="text-sm text-gray-500 hover:text-gray-700 hover:underline">
                  Clear all
                </button>
              )}
            </div>
          </div>
        </div>

        {underUser && (
          <div className="border-t border-gray-200 px-4 py-2 sm:px-6 flex items-center gap-2 bg-primary-50">
            <span className="text-sm text-primary-800">
              Showing the team under <span className="font-semibold">{underUser.firstName} {underUser.lastName}</span>
              {underUser.userCode && <span className="font-mono text-xs ml-1">({underUser.userCode})</span>}
            </span>
            <button onClick={() => handleFilterChange('under', '')} className="p-0.5 text-primary-700 hover:text-primary-900" title="Show everyone">
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        )}

        {showFilters && (
          <div className="border-t border-gray-200 px-4 py-4 sm:px-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mobile number</label>
                <input
                  type="tel"
                  value={filters.phone}
                  onChange={(e) => handleFilterChange('phone', e.target.value)}
                  placeholder="e.g. 98765"
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="text"
                  value={filters.email}
                  onChange={(e) => handleFilterChange('email', e.target.value)}
                  placeholder="e.g. name@company.com"
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">User ID</label>
                <input
                  type="text"
                  value={filters.userCode}
                  onChange={(e) => handleFilterChange('userCode', e.target.value)}
                  placeholder="e.g. DLR-0007"
                  className="input"
                />
              </div>
              {teamLeads.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Team of</label>
                  <select
                    value={filters.under}
                    onChange={(e) => handleFilterChange('under', e.target.value)}
                    className="input"
                  >
                    <option value="">Everyone</option>
                    {teamLeads.map(u => (
                      <option key={u._id} value={u._id}>
                        {u.firstName} {u.lastName} — {ROLE_LABELS[u.role]}{u.userCode ? ` (${u.userCode})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {filterableRoles.length > 1 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select
                    value={filters.role}
                    onChange={(e) => handleFilterChange('role', e.target.value)}
                    className="input"
                  >
                    <option value="">All Roles</option>
                    {filterableRoles.map(role => (
                      <option key={role} value={role}>{ROLE_LABELS[role]}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="input"
                >
                  <option value="">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Users Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <Spinner size="lg" />
            <p className="mt-2 text-gray-500">Loading users...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center">
            <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              No {currentUser.role === 'manager' ? 'salespersons' : 'users'} found
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {filtersActive
                ? 'Try adjusting your filters'
                : 'Get started by creating a new user'}
            </p>
            {hasPermission('users', 'create') && (
              <div className="mt-6">
                <Link to="/users/new" className="btn-primary">
                  <PlusIcon className="h-4 w-4 mr-2" />
                  {currentUser.role === 'manager' ? 'Add Salesperson' : 'Add User'}
                </Link>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">User</th>
                    <th className="table-header-cell">User ID</th>
                    <th className="table-header-cell">Contact</th>
                    <th className="table-header-cell">Role</th>
                    <th className="table-header-cell">Brands</th>
                    <th className="table-header-cell">Reports To</th>
                    <th className="table-header-cell">Status</th>
                    <th className="table-header-cell">Last Login</th>
                    <th className="table-header-cell">Actions</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {users.map((user) => {
                    const isSelf = String(user._id) === String(currentUser._id);
                    const canManage = canManageUser(currentUser.role, user.role);
                    const canEditUser = hasPermission('users', 'update') && canManage && !isSelf;

                    return (
                      <tr key={user._id} className="table-row">
                        <td className="table-cell">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-primary-500 flex items-center justify-center">
                                <span className="text-white font-medium">
                                  {user.firstName[0]}{user.lastName[0]}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {user.firstName} {user.lastName}
                              </div>
                              {isSelf && (
                                <div className="text-xs text-primary-600">You</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="table-cell">
                          <span className="text-sm font-mono text-gray-700">
                            {user.userCode || '—'}
                          </span>
                        </td>
                        <td className="table-cell">
                          <div className="space-y-1">
                            <div className="flex items-center text-sm text-gray-900">
                              <EnvelopeIcon className="h-4 w-4 mr-1 text-gray-400" />
                              {user.email}
                            </div>
                            {user.phone && (
                              <div className="text-sm text-gray-500">{user.phone}</div>
                            )}
                          </div>
                        </td>
                        <td className="table-cell">{getRoleBadge(user.role)}</td>
                        <td className="table-cell">
                          {user.role === 'dealer' && Array.isArray(user.brands) && user.brands.length > 0 ? (
                            <div className="flex flex-wrap gap-1 max-w-xs">
                              {user.brands.map(b => (
                                <span key={b._id || b} className="inline-flex items-center px-2 py-0.5 bg-primary-50 text-primary-700 text-xs font-medium rounded">
                                  {b.name || '—'}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">—</span>
                          )}
                        </td>
                        <td className="table-cell">
                          <div className="text-sm text-gray-600">
                            {user.parentId
                              ? `${user.parentId.firstName || ''} ${user.parentId.lastName || ''}`.trim() || '—'
                              : '—'}
                          </div>
                        </td>
                        <td className="table-cell">{getStatusBadge(user.status)}</td>
                        <td className="table-cell">
                          <div className="text-sm text-gray-900">
                            {user.lastLogin ? format(new Date(user.lastLogin), 'MMM dd, yyyy') : 'Never'}
                          </div>
                          {user.lastLogin && (
                            <div className="text-sm text-gray-500">
                              {format(new Date(user.lastLogin), 'HH:mm')}
                            </div>
                          )}
                        </td>
                        <td className="table-cell">
                          <div className="flex items-center space-x-2">
                            {/* Edit */}
                            {canEditUser && (
                              <button
                                onClick={() => setEditingUser(user)}
                                className="text-secondary-600 hover:text-secondary-900"
                                title="Edit user"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                            )}
                            {/* Reset Password */}
                            {canEditUser && (
                              <button
                                onClick={() => setResettingPasswordUser(user)}
                                className="text-warning-600 hover:text-warning-900"
                                title="Reset password"
                              >
                                <KeyIcon className="h-4 w-4" />
                              </button>
                            )}
                            {/* View login credentials — upline only */}
                            {canEditUser && currentUser.role !== 'sales_executive' && !isSelf && (
                              <button
                                onClick={() => setViewingCredentialsUser(user)}
                                className="text-indigo-600 hover:text-indigo-900"
                                title="View login details"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </button>
                            )}
                            {/* Manage Permissions (super_admin/admin, for lower roles) */}
                            {(currentUser.role === 'super_admin' || currentUser.role === 'admin') &&
                             canManage && !isSelf && (
                              <button
                                onClick={() => setManagingPermissionsUser(user)}
                                className="text-primary-600 hover:text-primary-900"
                                title="Manage permissions"
                              >
                                <AdjustmentsHorizontalIcon className="h-4 w-4" />
                              </button>
                            )}
                            {/* Delete */}
                            {hasPermission('users', 'delete') && canManage && !isSelf && (
                              <button
                                onClick={() => handleDelete(user._id)}
                                className="text-danger-600 hover:text-danger-900"
                                disabled={deleteUserMutation.isLoading}
                                title="Delete user"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => handlePageChange(pagination.current - 1)}
                    disabled={pagination.current <= 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handlePageChange(pagination.current + 1)}
                    disabled={pagination.current >= pagination.pages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <p className="text-sm text-gray-700">
                    Showing{' '}
                    <span className="font-medium">{(pagination.current - 1) * 10 + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(pagination.current * 10, pagination.total)}
                    </span>{' '}
                    of <span className="font-medium">{pagination.total}</span> results
                  </p>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => handlePageChange(pagination.current - 1)}
                      disabled={pagination.current <= 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                      const page = i + 1;
                      return (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            page === pagination.current
                              ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => handlePageChange(pagination.current + 1)}
                      disabled={pagination.current >= pagination.pages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      </>)}

      {/* Edit User Modal */}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          currentUserRole={currentUser.role}
          onSave={(id, data) => updateUserMutation.mutateAsync({ id, data })}
          onClose={() => setEditingUser(null)}
        />
      )}

      {/* Reset Password Modal */}
      {resettingPasswordUser && (
        <ResetPasswordModal
          user={resettingPasswordUser}
          onSave={(id, data) => resetPasswordMutation.mutateAsync({ id, data })}
          onClose={() => setResettingPasswordUser(null)}
        />
      )}

      {/* View Credentials Modal */}
      {viewingCredentialsUser && (
        <CredentialsModal
          user={viewingCredentialsUser}
          onClose={() => setViewingCredentialsUser(null)}
        />
      )}

      {/* Bulk Upload Modal */}
      {showBulkUpload && (
        <BulkUserUploadModal
          onClose={() => setShowBulkUpload(false)}
          onSuccess={() => {
            queryClient.invalidateQueries('users');
            queryClient.invalidateQueries('hierarchyUsers');
          }}
        />
      )}

      {/* Manage Permissions Modal */}
      {managingPermissionsUser && (
        <UserPermissionsModal
          user={managingPermissionsUser}
          grantorRole={currentUser.role}
          grantorPermissions={currentUser.permissions}
          onSave={(id, permissions) => updatePermissionsMutation.mutateAsync({ id, permissions })}
          onClose={() => setManagingPermissionsUser(null)}
        />
      )}
    </div>
  );
};
